<template>
  <v-card>
    <v-form ref="form" lazy-validation>
      <v-card-title>
        <span class="display-1">Ändra order</span>
      </v-card-title>

      <v-card-text>
        <div>
          <div v-for="course in courses" :key="course.id">
            <div class="d-flex justify-space-between align-center">
              <v-checkbox :label="course.name" v-model="rowsValue[course.remoteId].selected" @change="(v) => onCourseChange(course.remoteId, v)"/>
              <div>{{getRowPrice(course.remoteId) | currency | withCurrencyCode}}</div>  
            </div>
            <template v-if="rowsValue[course.remoteId].hasCourseOpportunities">
              <div class="pl-6">
                <div v-if="rowsValue[course.remoteId].existingCourseOpportunity">
                  <v-checkbox class="mt-0" 
                    :label="`Använd existerande (${rowsValue[course.remoteId].existingCourseOpportunity})`"
                    v-model="rowsValue[course.remoteId].useExistingCourseOpportunity"
                  />
                </div>
                <div v-if="!rowsValue[course.remoteId].useExistingCourseOpportunity">
                  <v-select
                    label="Kurstillfälle"
                    v-model="rowsValue[course.remoteId].courseOpportunity"
                    :items="course.courseOpportunities || []"
                    :rules="[getCourseOpportunityRule(course.remoteId)]"
                  />
                </div>
              </div>
            </template>
            <div class="pl-6" v-if="course.assignments && course.assignments.length">
              <div v-for="assignment in course.assignments" :key="`${course.id}_${assignment.id}`">
                <v-checkbox class="mt-0" 
                  :label="assignment.name" 
                  v-model="rowsValue[course.remoteId].assignments" 
                  :value="assignment.id" 
                  @change="(v) => onAssignmentsChange(course.remoteId, v)"
                />
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
        <div>
          <pre>
          {{rowsValue}}
          </pre>
        </div>
      <v-card-actions class="justify-end">
        <v-btn color="secondary" @click="close">
          <v-icon>$sl-cancel</v-icon>Avbryt
        </v-btn>
        <v-btn @click="save">
          <v-icon>$sl-check</v-icon>Spara
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import API from '@/services/api.js'
import { RulesMixin } from '@/components/mixins'
const SugarDate = require('sugar/date')

const PREFIX_NULL_REMOTE_ID = '_null';

export default {
  name: 'OrderAddOrderRow',

  props: {
    distanceOrderRows: { type: Array, default: () => [] },
    professionId: { type: Number, required: true },
    orderId: { type: Number, required: true  },
  },
  
  mixins: [RulesMixin],

  data: () => ({
    courses: [],
    rowsValue: {},
  }),

  created() {
    this.fetchCourses(this.professionId)
  },

  methods: {
    getCourseOpportunityRule(courseId){
      if(!this.rowsValue[courseId].selected){
        return true
      }

      return this.rules.required
    },

    getRowPrice(courseId){
      const course = this.courses.find(c => c.remoteId === courseId)
      if(!course){
        return 0
      }
      if(!course.assignments){
        return course.price
      }
      const assignmentsSum = course.assignments.reduce((acc, curr) => {
        if(!this.rowsValue[course.remoteId].assignments.includes(curr.id)){
          return acc
        }
        return acc + (curr.price || 0)
      }, 0)

      return Math.min(assignmentsSum || course.price, course.price)
    },

    onAssignmentsChange(remoteId, values){
      const course = this.courses.find(c => c.remoteId === remoteId)
      if(!course){
        return
      }
      this.rowsValue[remoteId].selected = course.assignments.length === values.length
    },

    onCourseChange(remoteId, value){
      if(!this.rowsValue[remoteId].assignments){
        return
      }
      if(!value){
        this.rowsValue[remoteId].assignments = []
      }else{
        const course = this.courses.find(c => c.remoteId === remoteId)
        if(course){
          this.rowsValue[remoteId].assignments = course.assignments.map(a => a.id) 
        }
      }
    },

    setSelectedCourse() {
      this.courses.forEach(course => {
          const distanceOrderRow = this.distanceOrderRows.find(d => (d.remoteId || `${d.id}${PREFIX_NULL_REMOTE_ID}`) === course.remoteId);

          const hasAssignments = course.assignments;
          const hasCourseOpportunities = (course.courseOpportunities && !!course.courseOpportunities.length) || (!!distanceOrderRow && !!distanceOrderRow.courseOpportunity);
          if(!distanceOrderRow){
            this.$set(this.rowsValue, course.remoteId, { 
              selected: false, 
              assignments: hasAssignments ? [] : null,
              hasCourseOpportunities,
              courseOpportunity: null,
              existingCourseOpportunity: null,
              useExistingCourseOpportunity: false
            })
            return
          }

          this.$set(this.rowsValue, course.remoteId, { 
            selected: hasAssignments ? (distanceOrderRow.assignments || []).length === (course.assignments || []).length : true, 
            assignments: hasAssignments ? (distanceOrderRow.assignments || []).map(a => a.id) : null,
            hasCourseOpportunities,
            courseOpportunity: null,
            existingCourseOpportunity: distanceOrderRow.courseOpportunity,
            useExistingCourseOpportunity: true
          })
      })
    },

    fetchCourses(id) {
      API.listFetchCourses(id)
        .then(response => {
          this.courses = [
            ...this.distanceOrderRows.filter(d => !d.remoteId).map(d => ({...d, remoteId: `${d.id}${PREFIX_NULL_REMOTE_ID}`})),
            ...response.data.map(course => ({
              ...course,
              courseOpportunities: (course.courseOpportunities || []).map(op => {
                const [dateString, description] = op.split(' - ');
                const formattedDate = SugarDate.Date(dateString).format('v. {W} {yyyy}').raw
                return [formattedDate, description].join(' - ');
              })
            }))
          ]
          this.setSelectedCourse()
        })
        .catch(error => {
          console.log('There was an error:', error.response)
        })
    },

    close() {
      this.$emit('close')
    },

    save() {
      if (!this.$refs.form.validate()) {
        this.$events.fire('showError', 'Formuläret är inte korrekt ifyllt.')
        return
      }

      const getCourseOpportunity = (rowValue) => {
        if(!rowValue.hasCourseOpportunities){
          return null
        }
        return rowValue.useExistingCourseOpportunity ? rowValue.existingCourseOpportunity : rowValue.courseOpportunity;
      }

      const distanceOrderRows = this.courses.reduce((acc, curr) => {
        const rowValue = this.rowsValue[curr.remoteId];
        if(!rowValue.selected && !(rowValue.assignments || []).length){
          return acc
        }
        const remoteId = curr.remoteId.includes(PREFIX_NULL_REMOTE_ID) ? null : curr.remoteId;

        acc.push({
          remoteId,
          name: curr.name,
          price: this.getRowPrice(curr.remoteId),
          courseOpportunity: getCourseOpportunity(rowValue),
          distanceOrderId: this.orderId,
          assignments: curr.assignments ? curr.assignments.filter(a => rowValue.assignments.includes(a.id)).map(({id, name}) => ({id, name})) : null
        })
        return acc
      }, [])

      API.updateDistanceOrder(this.orderId, {
        data: { distanceOrderRows }
      })
        .then(() => {
          this.$emit('save')
        })
        .catch(error => {
          this.$events.fire('showError', 'Ett fel uppstod (' + error.message + '), om detta händer igen v.v. kontakta BYN:s kansli.')
          console.log('There was an error:', error)          
        })
    }
  }
}
</script>
<style lang="scss">
.v-textarea.v-text-field--enclosed .v-text-field__slot textarea {
  line-height: 1.6;
}
</style>